import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Badge, Pagination, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getApi } from '../../api/api';
import { connect } from 'react-redux';
import PageTitle from '../layouts/PageTitle';
import Loading from '../components/Loading';
import binanceWS from '../../services/binance';
import TradeDetailsModal from '../components/TradeDetailsModal';

const CryptoTrades = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [trades, setTrades] = useState([]);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [status, setStatus] = useState('active');
    const [sortBy, setSortBy] = useState('openDate');
    const [sortOrder, setSortOrder] = useState(-1);
    const limit = 1000;
    const [marketData, setMarketData] = useState({});
    const wsRef = useRef(null);
    const [stats, setStats] = useState(null);
    const [livePnL, setLivePnL] = useState({});
    const [selectedTrade, setSelectedTrade] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const CRYPTO_PAIRS = [
        'BINANCE:BTCUSDT', 'BINANCE:ETHUSDT', 'BINANCE:BNBUSDT', 'BINANCE:ADAUSDT',
        'BINANCE:DOGEUSDT', 'BINANCE:XRPUSDT', 'BINANCE:DOTUSDT', 'BINANCE:SOLUSDT'
    ];

    const fetchTrades = async (page) => {
        try {
            setLoading(true);
            setError(null);
            const response = await getApi(
                `trades/crypto?page=${page}&limit=${limit}&status=${status}&sortBy=openDate&sortOrder=-1`
            );
            
            if (response.success) {
                // Sort trades by openDate in descending order (latest first)
                const sortedTrades = response.data.trades.sort((a, b) => 
                    new Date(b.openDate) - new Date(a.openDate)
                );
                
                setTrades(sortedTrades);
                setTotalPages(response.data.pagination.pages || 1);
                // setStats(response.data.stats);
            } else {
                throw new Error(response.message || 'Failed to fetch trades');
            }
        } catch (error) {
            console.error('Error fetching crypto trades:', error);
            setError(error.message);
            if (error.response?.status === 401) {
                navigate('/login', { replace: true });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (auth.isAuthenticated && auth.token) {
            fetchTrades(currentPage);
        } else {
            navigate('/login', { replace: true });
        }
    }, [auth, currentPage, status, sortBy, sortOrder, navigate]);

    // Calculate live P/L for active trades
    const calculateLivePnL = (trade, currentPrice) => {
        if (!trade || !currentPrice || trade.status !== 'active') return null;
        
        const priceDiff = trade.direction === 'buy' 
            ? currentPrice - trade.entryPrice
            : trade.entryPrice - currentPrice;
            
        return (priceDiff * trade.quantity).toFixed(2);
    };

    useEffect(() => {
        // Subscribe to market data for active trades
        if (trades.length > 0) {
            const symbols = [...new Set(trades.map(trade => trade.symbol))];
            
            // Subscribe to each symbol
            symbols.forEach(symbol => {
                binanceWS.addSymbol(symbol, (tickerData) => {
                    setMarketData(prev => ({
                        ...prev,
                        [tickerData.symbol]: tickerData
                    }));

                    // Update live P/L for active trades
                    setLivePnL(prev => {
                        const newPnL = { ...prev };
                        trades.forEach(trade => {
                            if (trade.symbol === tickerData.symbol && trade.status === 'active') {
                                newPnL[trade._id] = calculateLivePnL(trade, tickerData.lastPrice);
                            }
                        });
                        return newPnL;
                    });
                });
            });

            // Cleanup function
            return () => {
                symbols.forEach(symbol => {
                    binanceWS.removeSymbol(symbol);
                });
            };
        }
    }, [trades]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleStatusChange = (newStatus) => {
        setStatus(newStatus);
        setCurrentPage(1);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder * -1);
        } else {
            setSortBy(field);
            setSortOrder(-1);
        }
        setCurrentPage(1);
    };

    if (loading) return <Loading />;

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    return (
        <>
            <PageTitle activeMenu="Crypto Trades" motherMenu="Trading" />

            {/* TradingView Chart */}
            <div className="row mb-4">
                <div className="col-12">
                    <Card>
                        <Card.Body className="p-0">
                            <div className="tradingview-chart">
                                <iframe
                                    src="https://www.tradingview-widget.com/embed-widget/advanced-chart/?locale=en#%7B%22allow_symbol_change%22%3Atrue%2C%22calendar%22%3Afalse%2C%22details%22%3Afalse%2C%22autosize%22%3Atrue%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%2C%22hide_side_toolbar%22%3Atrue%2C%22hide_top_toolbar%22%3Afalse%2C%22hide_legend%22%3Afalse%2C%22hide_volume%22%3Afalse%2C%22hotlist%22%3Afalse%2C%22interval%22%3A%22D%22%2C%22save_image%22%3Atrue%2C%22studies%22%3A%5B%5D%2C%22style%22%3A%221%22%2C%22symbol%22%3A%22BINANCE%3ABTCUSDT%22%2C%22theme%22%3A%22light%22%2C%22timezone%22%3A%22Etc%2FUTC%22%2C%22backgroundColor%22%3A%22%23FFFFFF%22%2C%22gridColor%22%3A%22rgba(240%2C%20243%2C%20250%2C%200.06)%22%2C%22watchlist%22%3A%5B%5D%2C%22show_popup_button%22%3Afalse%2C%22popup_width%22%3A%221000%22%2C%22popup_height%22%3A%22650%22%2C%22compareSymbols%22%3A%5B%5D%2C%22support_host%22%3A%22https%3A%2F%2Fwww.tradingview.com%22%2C%22utm_source%22%3A%22www.tradingview.com%22%2C%22utm_medium%22%3A%22widget_new%22%2C%22utm_campaign%22%3A%22advanced-chart%22%2C%22page-uri%22%3A%22www.tradingview.com%2Fwidget-wizard%2Fen%2Flight%2Fadvanced-chart%2F%22%7D"
                                    style={{
                                        width: "100%",
                                        height: "600px",
                                        border: "none"
                                    }}
                                    allowTransparency="true"
                                    scrolling="no"
                                    allowFullScreen
                                    frameBorder="0"
                                ></iframe>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Stats and Trades Table */}
            {stats && (
                <div className="row mb-4">
                    <Col md={4}>
                        <Card>
                            <Card.Body>
                                <h4>Total Trades</h4>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h2>{stats.totalTrades}</h2>
                                    <div>
                                        <div className="text-success">Win: {stats.profitTrades}</div>
                                        <div className="text-danger">Loss: {stats.lossTrades}</div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card>
                            <Card.Body>
                                <h4>Total Profit</h4>
                                <h2 className="text-success">${stats.totalProfit.toFixed(2)}</h2>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card>
                            <Card.Body>
                                <h4>Total Loss</h4>
                                <h2 className="text-danger">${stats.totalLoss.toFixed(2)}</h2>
                            </Card.Body>
                        </Card>
                    </Col>
                </div>
            )}

            <Card>
                <Card.Header>
                    <Card.Title>Crypto Trades</Card.Title>
                    <div className="d-flex gap-2">
                        <Badge 
                            bg={status === 'active' ? 'primary' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStatusChange('active')}
                        >
                            Active
                        </Badge>
                        <Badge 
                            bg={status === 'completed' ? 'primary' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStatusChange('completed')}
                        >
                            Completed
                        </Badge>
                        {/* <Badge 
                            bg={status === 'cancelled' ? 'primary' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStatusChange('cancelled')}
                        >
                            Cancelled
                        </Badge> */}
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>Symbol</th>
                                    <th>Direction</th>
                                    <th>Quantity</th>
                                    <th>Entry Price</th>
                                    <th>Target</th>
                                    <th>Stop Loss</th>
                                    <th>Current Price</th>
                                    <th>Live P/L</th>
                                    <th>24h Change</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trades.map(trade => (
                                    <tr key={trade._id}>
                                        <td>
                                            <Badge bg="primary">
                                                {trade?.symbol || 'N/A'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={trade?.direction === 'buy' ? 'success' : 'danger'}>
                                                {(trade?.direction || 'unknown').toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>{trade?.quantity || 0}</td>
                                        <td>${trade?.entryPrice?.toFixed(2) || '0.00'}</td>
                                        <td>${trade?.targetPrice?.toFixed(2) || '0.00'}</td>
                                        <td>${trade?.stopLoss?.toFixed(2) || '0.00'}</td>
                                        <td>
                                            {marketData[trade?.symbol] ? (
                                                <span className={marketData[trade.symbol].priceChange >= 0 ? 'text-success' : 'text-danger'}>
                                                    ${marketData[trade.symbol].lastPrice.toFixed(2)}
                                                </span>
                                            ) : (
                                                'Loading...'
                                            )}
                                        </td>
                                        <td>
                                            {trade.status === 'active' ? (
                                                <span className={
                                                    !livePnL[trade._id] ? '' :
                                                    parseFloat(livePnL[trade._id]) >= 0 ? 'text-success' : 'text-danger'
                                                }>
                                                    ${livePnL[trade._id] || '0.00'}
                                                </span>
                                            ) : (
                                                <span className={
                                                    !trade.profitLoss ? '' :
                                                    trade.profitLoss >= 0 ? 'text-success' : 'text-danger'
                                                }>
                                                    ${trade.profitLoss?.toFixed(2) || '0.00'}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {marketData[trade?.symbol] && (
                                                <span className={marketData[trade.symbol].priceChange >= 0 ? 'text-success' : 'text-danger'}>
                                                    {marketData[trade.symbol].priceChangePercent.toFixed(2)}%
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <Badge bg={
                                                !trade?.status ? 'secondary' :
                                                trade.status === 'active' ? 'warning' : 
                                                trade.status === 'completed' ? 'success' :
                                                trade.status === 'cancelled' ? 'danger' : 'secondary'
                                            }>
                                                {(trade?.status || 'unknown').toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>
                                            {trade?.openManualDate + ' ' + trade?.openManualTime}
                                        </td>
                                        <td>
                                            <Button 
                                                variant="light" 
                                                size="sm"
                                                style={{ 
                                                    backgroundColor: '#132720',
                                                    color: '#fff'  // Adding white text for better contrast
                                                }}
                                                onClick={() => {
                                                    setSelectedTrade(trade);
                                                    setShowDetailsModal(true);
                                                }}
                                            >
                                                Details
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                
                                {[...Array(totalPages)].map((_, index) => (
                                    <Pagination.Item
                                        key={index + 1}
                                        active={currentPage === index + 1}
                                        onClick={() => handlePageChange(index + 1)}
                                    >
                                        {index + 1}
                                    </Pagination.Item>
                                ))}
                                
                                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <TradeDetailsModal 
                show={showDetailsModal}
                onHide={() => {
                    setShowDetailsModal(false);
                    setSelectedTrade(null);
                }}
                trade={selectedTrade}
            />

            <style jsx>{`
                .tradingview-chart {
                    width: 100%;
                    background: #fff;
                    border-radius: 8px;
                    overflow: hidden;
                }
                @media (max-width: 768px) {
                    .tradingview-chart iframe {
                        height: 400px !important;
                    }
                }
            `}</style>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(CryptoTrades); 